/**
 * `growth` — portfolio ADS kit (canon: listto). Contract + model.
 *
 * Responsibility split (so every app does not reinvent it):
 *   - **`AdProvider`** (this layer, `provider.ts`) decides *whether it CAN*:
 *     environment flags, user tier, platform, available network. It is the
 *     only one that talks to an SDK.
 *   - **`adGate`** (`gate.ts`) decides *whether it SHOULD*: breakpoints,
 *     frequency capping, child policy. PURE function, no SDK or clock of its own.
 *   - **`AdCapsStore`** (`caps.ts`) keeps the counters **local-first**
 *     (`ads.cap.<placement>.<day>`): works offline and for guests.
 *
 * 100% domain-AGNOSTIC kit: it does not know what a list, habit, or lesson is.
 * Each app provides its placement registry and rewards.
 *
 * **OFF by default**: without `ADS_ENABLED`/`VITE_ADS_ENABLED` the whole layer is
 * inert and NO SDK is loaded (zero network requests — the privacy promise is
 * technical, not cosmetic).
 */

/** Supported formats, in the portfolio's order of preference. */
export const AD_KINDS = ["rewarded", "native", "interstitial", "banner"] as const;

export type AdKind = (typeof AD_KINDS)[number];

/** Breathing moments where an ad is allowed. */
export const AD_BREAKPOINTS = [
  "done-screen",
  "session-end",
  "between-screens",
  "stats",
  "feed-slot",
  "reward-moment",
] as const;

export type AdBreakpointId = (typeof AD_BREAKPOINTS)[number];

/** App ad tier (portfolio classification). */
export const AD_TIERS = ["A", "K", "C", "D"] as const;

/**
 * - `A` — consumer: all formats.
 * - `K` — child-oriented: COPPA/Families regime (G banner + soft interstitial;
 *   rewarded requires an adult gate).
 * - `C` — B2B/institutional: ZERO ads.
 * - `D` — privacy: ZERO ads and zero bundled SDK.
 */
export type AdTier = (typeof AD_TIERS)[number];

/** Content ratings, **from strictest to most permissive** (the order is the range). */
export const AD_CONTENT_RATINGS = ["G", "PG", "T", "MA"] as const;

export type AdContentRating = (typeof AD_CONTENT_RATINGS)[number];

/**
 * **Tier regime floor**: the minimum the classification guarantees,
 * regardless of configuration.
 *
 * This is a DECLARATION, not a preference: `resolveAdsConfig` applies it at the
 * end of resolution (`clampAdsConfigToTier`), so neither an environment variable
 * nor a back-office override can go below it. It can only be hardened.
 */
export interface AdTierRegime {
  /** `false` ⇒ ZERO ads and zero SDK, even if `ADS_ENABLED` says otherwise. */
  readonly adsAllowed: boolean;
  /** `true` ⇒ mandatory child regime (COPPA / Play Families). */
  readonly childDirected: boolean;
  /** Rating ceiling: nothing above it can be served in this tier. */
  readonly maxAdContentRating: AdContentRating;
}

/**
 * Strictest possible regime. It is applied to an UNKNOWN tier:
 * fail-closed — if someone adds a tier to `AD_TIERS` and forgets its regime, the
 * app gets no ads instead of serving them without policy.
 */
export const STRICTEST_AD_TIER_REGIME: AdTierRegime = {
  adsAllowed: false,
  childDirected: true,
  maxAdContentRating: "G",
};

/**
 * Declared regime for each tier (§9 of the ads strategy).
 *
 * - `A` — adult consumer: no special floor (the gate provides capping).
 * - `K` — child-oriented: child-directed ALWAYS, rating G ALWAYS. This is the guarantee
 *   signed with Google (Play Families) and the FTC (COPPA).
 * - `C`/`D` — B2B and privacy: zero ads, zero SDK.
 *
 * ⚠️ CashSync is tier `A`, but **the tier is not its only floor**: the financial
 * DOMAIN floor (`monetization/ads-policy`) runs on top and prohibits behavioral
 * segmentation regardless. See `clampAdsConfigToTier`.
 */
export const AD_TIER_REGIMES: Readonly<Record<AdTier, AdTierRegime>> = {
  A: { adsAllowed: true, childDirected: false, maxAdContentRating: "MA" },
  K: { adsAllowed: true, childDirected: true, maxAdContentRating: "G" },
  C: { adsAllowed: false, childDirected: false, maxAdContentRating: "G" },
  D: { adsAllowed: false, childDirected: false, maxAdContentRating: "G" },
};

/** Rewarded reward: what the user gets, visible BEFORE seeing it. */
export interface AdReward {
  /** Stable reward type id (`streak-freeze`, `theme-unlock`, …). */
  type: string;
  amount: number;
  /** Validity of temporary rewards, in hours. Absent = consumable. */
  durationHours?: number;
}

/** Placement declaration in the app's typed registry. */
export interface PlacementDef {
  kind: AdKind;
  breakpoint: AdBreakpointId;
  /** Only for `rewarded`: what is redeemed. */
  reward?: AdReward;
}

/** Placement registry for an app (`Record<id, PlacementDef>`). */
export type PlacementRegistry = Record<string, PlacementDef>;

/** Result of initializing the provider (including COPPA flags). */
export interface AdInitOptions {
  childDirected?: boolean;
  maxAdContentRating?: AdContentRating;
  /**
   * Request NON-personalized ads from the SDK (CashSync extension to the
   * canon). Like the rest of the regime, it can only be HARDENED: passing
   * `false` does not relax what config already set (`hardenAdInitOptions`).
   */
  contextualOnly?: boolean;
  testDeviceIds?: string[];
}

/** Result of showing an ad. `reward` ONLY if the user completed it. */
export interface AdShowResult {
  shown: boolean;
  reward?: AdReward;
}

export type AdEventName = "impression" | "reward" | "dismiss" | "error";

export interface AdEvent {
  kind: AdKind;
  placementId: string;
  reward?: AdReward;
  error?: string;
}

/**
 * Ad provider contract. Implemented by a backend per network
 * (`none` = no-op and DEFAULT, `adsense` = web). The placement layer only talks
 * to this interface → changing networks does not touch a single screen.
 */
export interface AdProvider {
  /** Stable backend id (`none`, `adsense`, `admob`, …). */
  readonly id: string;
  /** Initializes the network. Idempotent; `none` does NOTHING (no request). */
  init(opts?: AdInitOptions): Promise<void>;
  /** `false` ⇒ the placement layer collapses entirely for that format. */
  isEnabled(kind: AdKind): boolean;
  /** Best-effort preload. `false` = no fill ⇒ the UI collapses, never a spinner. */
  prepare(kind: AdKind, placementId: string): Promise<boolean>;
  /** Shows the ad. For `rewarded`, resolves with the reward if completed. */
  show(kind: AdKind, placementId: string): Promise<AdShowResult>;
  /** Event subscription. Returns the unsubscribe function. */
  on(ev: AdEventName, cb: (e: AdEvent) => void): () => void;
}

/** Resolved ads configuration (env → admin UI overrides). */
export interface AdsConfig {
  /** Master switch. **false by default**. */
  enabled: boolean;
  /** Provider backend. `none` by default (no-op, no SDK). */
  network: string;
  /** App ad tier (§9 of the strategy). */
  tier: AdTier;
  /** Child regime: forces the strict policy in the gate. */
  childDirected: boolean;
  /** Maximum served content rating (ceiling, never exceedable by the tier). */
  maxAdContentRating: AdContentRating;
  /**
   * **CONTEXTUAL ads only** (CashSync extension to the canon kit).
   *
   * `true` ⇒ the SDK is explicitly asked for non-personalized advertising and
   * no signal derived from financial content can target. This is the domain floor
   * (`monetization/ads-policy`): **it cannot be set to
   * `false` by configuration**, neither by env, nor by override, nor through `init()`
   * options. It exists as a field —not a constant— so the resolution cascade has
   * a place to try to relax it and the clamp a place to prevent it (the only way
   * to have a test of the guarantee).
   */
  contextualOnly: boolean;
  /** Web network client id (e.g. `ca-pub-…`). Empty ⇒ no fill. */
  clientId: string;
}

/** Local day key `YYYY-MM-DD` (the caller decides the time zone). */
export type AdDayKey = string;

/**
 * Capping counters. They live **locally** (device) under
 * `ads.cap.<placement>.<day>` → they work offline and without an account.
 */
export interface CapsState {
  /** Day to which the daily counters belong. */
  day: AdDayKey;
  /** Interstitials shown today (all placements). */
  interstitialsToday: number;
  /** Interstitials shown in the current session. */
  interstitialsThisSession: number;
  /** Epoch ms of the last INTERRUPTIVE ad shown. */
  lastInterstitialAt?: number;
  /** Rewards granted today, by `reward.type` (anti-farming). */
  rewardsToday: Record<string, number>;
  /** Rewards granted today, by PLACEMENT (feeds backoff). */
  takenToday: Record<string, number>;
  /** Times the rewarded offer was shown today, by placement. */
  offersToday: Record<string, number>;
  /** Consecutive days with an offer shown and NO reward (backoff). */
  ignoredDays: Record<string, number>;
  /** Native ads already rendered on the current screen. */
  nativesOnScreen: number;
  /** Banners visible right now. */
  bannersVisible: number;
}

/** Signals from the current session (provided by the app-shell). */
export interface AdSessionState {
  /** ACTIVE usage time accumulated in the session, in ms. */
  activeMs: number;
  /** Value units completed in the session (lists closed, …). */
  unitsCompleted: number;
  /** Is this the user's very first session? (clean onboarding). */
  isFirstEver: boolean;
}

export interface AdUserState {
  /** Ad-free tier (resolved by `shouldShowAds` in the monetization kit). */
  premium: boolean;
  /** Surface used by a child ⇒ §6 regime. */
  child: boolean;
  /** Adult gate cleared (the only route to rewarded in the child regime). */
  adultGateCleared?: boolean;
}

export interface AdGateInput {
  kind: AdKind;
  placementId: string;
  /** Placement's declared breakpoint (for child policy). */
  breakpoint?: AdBreakpointId;
  /** Reward type, only `rewarded` (anti-farming by type). */
  rewardType?: string;
  /** Epoch ms. Injected: the gate is PURE. */
  now: number;
  caps: CapsState;
  session: AdSessionState;
  user: AdUserState;
  /** Active input / running timer / half-finished transaction ⇒ NEVER an ad. */
  inFlight: boolean;
  /** Numeric policy; defaults to the portfolio policy (§4). */
  policy?: Partial<AdCapPolicy>;
}

/** Stable reasons — feed telemetry and tests (not UI text). */
export type AdGateReason =
  | "allow"
  | "premium"
  | "in-flight"
  | "child-policy"
  | "first-session"
  | "no-value-unit"
  | "session-cap"
  | "daily-cap"
  | "min-gap"
  | "interruption-budget"
  | "reward-daily-cap"
  | "offer-daily-cap"
  | "offer-backoff"
  | "screen-cap";

export interface AdGateDecision {
  allow: boolean;
  reason: AdGateReason;
}

/** Numeric capping policy (portfolio defaults in `gate.ts`). */
export interface AdCapPolicy {
  interstitialPerSession: number;
  interstitialPerDay: number;
  /** Minimum distance between interstitials, in usage ms. */
  minGapBetweenInterstitialsMs: number;
  /** Is the first interstitial allowed in the 1st-ever session? */
  allowInterstitialOnFirstSession: boolean;
  /** Value units the session must have before an interstitial. */
  minUnitsCompleted: number;
  /** Maximum rewards per day BY TYPE (anti-farming). */
  rewardsPerDayPerType: number;
  /** Times an ignored rewarded offer is shown, per day and placement. */
  offersPerDayPerPlacement: number;
  /** Consecutive days ignoring the offer before reducing its frequency. */
  ignoredDaysBeforeBackoff: number;
  /** Reduced frequency (offers/day) once in backoff. */
  offersPerDayWhenBackedOff: number;
  nativePerScreen: number;
  bannersVisible: number;
  /** Global budget: 1 interruptive impression per X ms of active usage. */
  interruptionBudgetMs: number;
}
